import SideMenu from './SideMenu';
import SideMenuMobile from './SideMenu/SideMenuMobile';
import TopHeader from './TopHeader';
import {
    BoxBelowTopHeader,
    ContentBox,
    ContentContainer,
    ContentWrapper,
    StyledSpeedDial,
    PageLayoutWrapperBox,
    SideMenuBox,
    MobileMenuButtonContainer,
} from './EmployerPageLayout.styles';
import { ReactNode, useCallback, useState } from 'react';
import {
    useAppDispatch,
    useAppSelector,
    Theme,
    Drawer,
    IconButton,
    Box,
    useTheme,
    Icon,
} from '../../base';
import { useMediaQuery, SpeedDialAction, SpeedDialIcon } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../routes/RoutePaths';
import {
    Directions,
    jobStepperInfoInitState,
    FeatureFlags,
} from '../../common';
import { jobsActions, organizationSelectors } from '../../actions';
import ApplyNowModal from '../ApplyNowModal';
import ApplyNowModalV2 from '../ApplyNowModalV2';
import { RootState } from '../../reducers';

interface IEmployerPageLayoutProps {
    children: ReactNode;
}
const EmployerPageLayout = (props: IEmployerPageLayoutProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('md')
    );
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isSpeedDialOpen, setSpeedDialOpen] = useState(false);
    const [applyNowModalOpen, setApplyNowModalOpen] = useState(false);
    const isCandidateQuestionsFeatureFlagOn = useAppSelector(
        (state: RootState) =>
            !!organizationSelectors.isFlagOn(
                state,
                FeatureFlags.CANDIDATE_QUESTIONS
            )
    );

    const onAddJobClick = useCallback(() => {
        dispatch(jobsActions.setActiveJob({ job: null }));
        dispatch(
            jobsActions.setStepperInfo({
                stepperInfo: jobStepperInfoInitState,
            })
        );
        navigate(`${RoutePaths.jobs}${RoutePaths.saveJob}`);
    }, [dispatch, navigate]);

    const onAddCandidateClick = useCallback(() => {
        setApplyNowModalOpen(true);
    }, []);

    const handleDrawerToggle = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    const actions = [
        {
            icon: <Icon name="JobsIcon" size={20} />,
            tooltipTitle: t('speedDial.createNewJob'),
            onClick: onAddJobClick,
        },
        {
            icon: <Icon name="CandidatesIcon" size={20} />,
            tooltipTitle: t('speedDial.addCandidateToJob'),
            onClick: () => {
                onAddCandidateClick();
            },
        },
    ];

    const renderAddJobButton = (sxOverride?: object) => (
        <StyledSpeedDial
            ariaLabel=""
            icon={
                <SpeedDialIcon
                    icon={
                        <Icon
                            name="AddRoundedIcon"
                            size={34}
                            sx={{ color: theme.palette.common.white }}
                            noWrapper
                        />
                    }
                    openIcon={
                        <Icon
                            name="CloseIcon"
                            size={34}
                            sx={{ color: theme.palette.common.white }}
                            noWrapper
                        />
                    }
                />
            }
            onClose={() => setSpeedDialOpen(false)}
            onOpen={() => setSpeedDialOpen(true)}
            open={isSpeedDialOpen}
            FabProps={{
                size: 'large',
                sx: sxOverride,
            }}
        >
            {actions.map((action) => (
                <SpeedDialAction
                    key={action.tooltipTitle}
                    icon={action.icon}
                    tooltipTitle={action.tooltipTitle}
                    onClick={action.onClick}
                    FabProps={{
                        sx: {
                            backgroundColor: theme.palette.grey[400],
                            '&:hover': {
                                backgroundColor: theme.palette.grey[500],
                            },
                            color: theme.palette.common.white,
                        },
                    }}
                />
            ))}
        </StyledSpeedDial>
    );

    const renderMobileMenuToggle = () => (
        <IconButton
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            sx={{
                height: '48px',
                width: '48px',
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.common.white,
                '&:hover': {
                    bgcolor: theme.palette.secondary.dark,
                },
            }}
        >
            <MenuIcon />
        </IconButton>
    );

    const renderSideMenu = () => {
        if (isMobile) {
            return (
                <Drawer
                    anchor={
                        theme.direction === Directions.rtl ? 'right' : 'left'
                    }
                    open={isDrawerOpen}
                    onClose={handleDrawerToggle}
                >
                    <Box
                        sx={{ width: 250, height: '100%' }}
                        role="presentation"
                    >
                        <SideMenuMobile onClose={handleDrawerToggle} />
                    </Box>
                </Drawer>
            );
        }
        return (
            <SideMenuBox>
                <SideMenu />
            </SideMenuBox>
        );
    };

    return (
        <PageLayoutWrapperBox>
            {renderSideMenu()}
            <ContentWrapper>
                <TopHeader />
                <BoxBelowTopHeader>
                    <ContentBox>
                        <ContentContainer>{props.children}</ContentContainer>
                    </ContentBox>
                    {!isMobile && renderAddJobButton()}
                    {isMobile && (
                        <MobileMenuButtonContainer>
                            {renderAddJobButton({
                                position: 'relative',
                                bottom: 'auto',
                                insetInlineStart: 'auto',
                            })}
                            {renderMobileMenuToggle()}
                        </MobileMenuButtonContainer>
                    )}
                </BoxBelowTopHeader>
            </ContentWrapper>
            {isCandidateQuestionsFeatureFlagOn ? (
                <ApplyNowModalV2
                    isModalOpen={applyNowModalOpen}
                    setIsModalOpen={setApplyNowModalOpen}
                    isEmployerPortal
                />
            ) : (
                <ApplyNowModal
                    isModalOpen={applyNowModalOpen}
                    setIsModalOpen={setApplyNowModalOpen}
                    isEmployerPortal
                />
            )}
        </PageLayoutWrapperBox>
    );
};

export default EmployerPageLayout;
